import React, { useRef, useState, KeyboardEvent, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGlobalState } from "../../contexts/GlobalContext";
import { Http, Routes } from "../../utils/constants";
import { authHelper } from "../../utils/helpers";
import Input from "../../components/Commons/Input";
import SolidArrow from "../../components/svg/SolidArrow";
import { fetchGetAuthorize, fetchGetNewToken, fetchGetNewTokenSpa } from "../../utils/helpers/auth0";
import {
  fetchLogin,
  fetchSessionProxy,
  fetchUpdateUserMeataData,
  fetchUserInfo,
} from "../../utils/helpers/api";
import { AxiosResponse } from "axios";
import { needsExternalSession } from "../../utils/helpers/string";
import { isAdverTimesBulkDownloadFlow } from "../../utils/helpers/function";
import { Close } from "@mui/icons-material";
import { t } from "i18next";
import { jwtDecode } from "jwt-decode";
import { handleRedirect } from "../../utils/helpers/function";

interface ILogin {
  email?: string;
  password?: string;
}
// NOTE: Layoutでパスやグローバルユーザーが変わった時に同様の処理をしているので、共通化すべき
const Login: React.FC = () => {
  const [state, setState] = useState<ILogin>({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    loginErr: "",
    isValid: false,
  });
  const [isShowErrLogin, setIsShowErrLogin] = useState<boolean>(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { setStateValue } = useGlobalState();
  const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
       const urlParams = new URLSearchParams(window.location.search);
       const trackingInflowRoute = urlParams.get("returnto");
       if(trackingInflowRoute){
         localStorage.setItem("trackingInflowRoute",trackingInflowRoute);
       }
    }, []);

  const handleClickVisibleErrLogin = () => {
    setIsShowErrLogin(false);
  };

  const handlerChangeValue = (field: string, value: string) => {
    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  // NOTE: 必要？
  const validateForm = () => {
    let isValid = true;
    let newErrors = { email: "", isValid: true };

    if (state?.email) {
      if (!state.email.includes("@")) {
        newErrors.email = `メールアドレスに「＠」を挿入してください。「${state.email}」内に「＠」がありません`;
        isValid = false;
      } else {
        const atIndex = state.email.indexOf("@");
        const beforeAt = state.email.substring(0, atIndex);
        const afterAt = state.email.substring(atIndex + 1);
        const regex = /[^a-zA-Z0-9!#$%^&*=~^|`'{}_*?.,-]/g;
        const invalidChars = beforeAt.match(regex) || [];
        if (beforeAt.length === 0) {
          newErrors.email = `「＠」の前の文字列を入力してください。「${state.email}」は完全なメールアドレスではありません。`;
          isValid = false;
        } else if (afterAt.length === 0) {
          newErrors.email = `「${state.email}」は完全なメールアドレスではありません。「＠」に続く文字列を入力してください。`;
          isValid = false;
        } else if (invalidChars.length > 0) {
          newErrors.email = `「＠」の前の文字列に記号「${invalidChars.join(
            ", "
          )}」を使用しないでください。`;
          isValid = false;
        }
      }
    }

    setErrors((prevState) => ({
      ...prevState,
      email: newErrors.email,
      isValid: newErrors.isValid,
    }));
    return isValid;
  };

  const handlerSubmit = async () => {
    const lowerCaseEmail = (state.email ?? "").toLowerCase();
    localStorage.setItem("isGoogleLogin", "");
    localStorage.setItem("isLogin", "true");
    if (emailInputRef.current) {
      if (!emailInputRef.current.checkValidity()) {
        emailInputRef.current.reportValidity();
        return;
      }
      if (!lowerCaseEmail || !state.password) {
        setErrors((prevState) => ({
          ...prevState,
          loginErr: "IDもしくはパスワードが合致しません。",
        }));
        setIsShowErrLogin(true);
        return;
      }
      setStateValue("loader", true);
      try {
        const result = await fetchLogin(lowerCaseEmail, state.password);
        if (result?.status === Http.SUCCESS_CODE) {
          const { access_token, expires_in, refresh_token } = (
            result as AxiosResponse
          ).data;
          const urlParams = new URLSearchParams(window.location.search);
          // Prefer URL returnto; fall back to storage (survives OAuth / localStorage.clear on survey guest)
          const loginCallBackUrl =
            urlParams.get("returnto") ||
            localStorage.getItem("loginCallBackUrl");
          authHelper.setAccessToken(access_token, expires_in);
          authHelper.setValueToCookie("_rftk", refresh_token, "31536000");
          authHelper.setValueToCookie("_exp_in", expires_in, "31536000");
          const currentUrl = window.location.href;
          try {
            if (
              !loginCallBackUrl ||
              !needsExternalSession(currentUrl ?? "", loginCallBackUrl)
            ) {
              await fetchSessionProxy({
                access_token,
                expires_in,
                refresh_token,
              });
            }
          } catch (error) {
            return error;
          } finally {
            if (!loginCallBackUrl) {
              const isUserSetting = localStorage.getItem("isUserSetting");
              const surveyId = localStorage.getItem("surveyId");
              const survey_url = localStorage.getItem("survey_url");
              const serviceid_url = localStorage.getItem("serviceid_url");
              const email_change = localStorage.getItem("email_change");
              if (isUserSetting) {
                navigate(Routes.USERSETTING);
                localStorage.removeItem("isUserSetting");
              } else if (surveyId) {
                navigate(Routes.SURVEY, {
                  state: { survey_id: surveyId },
                });
                localStorage.removeItem("surveyId");
              } else if (survey_url) {
                navigate(survey_url);
                localStorage.removeItem("survey_url");
                // NOTE: LayoutでloginCallbackUrlをつかってリダイレクトする処理があるので、ここで消す
                localStorage.removeItem("loginCallBackUrl");
              } else if (serviceid_url) {
                navigate(serviceid_url);
                localStorage.removeItem("serviceid_url");
              } else if (email_change) {
                navigate(email_change);
                localStorage.removeItem("email_change");
              } else {
                navigate(Routes.USER);
              }
            } else {
              // SDK_WEB-1374: bulk-download inflow only — AdverTimes returnto wins over survey_url
              const survey_url = localStorage.getItem("survey_url");
              if (survey_url && !isAdverTimesBulkDownloadFlow(loginCallBackUrl)) {
                navigate(survey_url);
                localStorage.removeItem("survey_url");
              } else {
                const decode = jwtDecode(access_token);
                await handleUserTransitionAndSession(
                  loginCallBackUrl,
                  decode?.sub ?? "",
                  refresh_token
                );
              }
            }
          }
        }
      } catch (err) {
        if (!(err instanceof Error)) {
          const result = err as AxiosResponse;
          if (result?.status === 403) {
            setErrors((prevState) => ({
              ...prevState,
              loginErr: "IDもしくはパスワードが合致しません。",
            }));
            setIsShowErrLogin(true);
            return;
          }
          const { error, error_description } = result.data;
          if (error && error_description) {
            const emailMatch = error_description?.match(/email:\s*([^\s]+)/);
            const email = emailMatch ? emailMatch[1] : null;

            if (email) {
              localStorage.setItem("email", email ?? "");
              navigate(Routes.EMAIL_SENT, { state: { email } });
            } else {
              setErrors((prevState) => ({
                ...prevState,
                loginErr: "IDもしくはパスワードが合致しません。",
              }));
              setIsShowErrLogin(true);
            }
          }
        }
      } finally {
        setStateValue("loader", false);
      }
    }
  };

  const handleSession = async (
    returnTo: string,
    refresh_token: string,
    user_status: number
  ) => {

    const currentUrl = window.location.href;
    if (needsExternalSession(currentUrl, returnTo)) {
      try {
        const isGoogleLogin = localStorage.getItem("isGoogleLogin");
        const response = isGoogleLogin
        ? await fetchGetNewTokenSpa(refresh_token)
        : await fetchGetNewToken(refresh_token);
        if (response) {
          const { access_token, expires_in, refresh_token: new_refresh_token } = response;
          authHelper.setAccessToken(access_token, expires_in);
          // localStorage.removeItem("isGoogleLogin");
          try {
            if (!document.getElementById("custom-overlay")) {
              const overlay = document.createElement("div");
              overlay.id = "custom-overlay";
              overlay.style.position = "fixed";
              overlay.style.top = "0";
              overlay.style.left = "0";
              overlay.style.width = "100%";
              overlay.style.height = "100%";
              overlay.style.backgroundColor = "rgba(128, 128, 128, 0.4)";
              overlay.style.zIndex = "9999";
              document.body.appendChild(overlay);
            }

            if (isGoogleLogin) {
              authHelper.setValueToCookie("_rftk", new_refresh_token, "31536000");
            } 

            const result = await fetchSessionProxy({
              access_token,
              expires_in,
              refresh_token: isGoogleLogin ? new_refresh_token : refresh_token,
              external: true,
            });

            const session_id = result?.data?.session_id ?? "";
            handleRedirect({ navigate, user_status, returnTo, session_id });
          } catch (error) {
            return error;
          }
        }
      } catch (error) {
        return error;
      }
    } else {
      localStorage.removeItem("session_id");
      localStorage.removeItem("loginCallBackUrl");
      if (user_status === 1 || user_status === 3) {
        navigate(Routes.USERSETTING + "?returnto=" + returnTo);
      } else {
        window.location.href = returnTo;
      }
    }
  };

  const handleUpdateTransitionSource = async (
    user_id: string,
    data: IUserMetaDataApi
  ) => {
    try {
      const res = await fetchUpdateUserMeataData(user_id ?? "", data);
      if (res) {
        localStorage.removeItem("transition_source");
      }
    } catch (err) {
      return err;
    }
  };

  const handleUserTransitionAndSession = async (
    returnTo: string,
    user_id: string,
    refresh_token: string
  ) => {
    const metadata = await fetchUserInfo();
    const { user_metadata } = metadata.data.userdata ?? {};
    const raw = user_metadata.transition_source || localStorage.getItem("transition_source");
    let transitionSource = null; 
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && "value" in parsed) {
          transitionSource = parsed.value;
        } else {
          transitionSource = raw;
        }
      } catch {
        transitionSource = raw;
      }
    }
    const dataUpdate: IUserMetaDataApi = user_metadata ?? {};

    if (transitionSource && (user_metadata.user_status === 1 || user_metadata.user_status === 3) && user_metadata.transition_source === "kankyobusiness") {
      dataUpdate.transition_source = transitionSource;
      await handleUpdateTransitionSource(user_id ?? "", dataUpdate);
    }

    await handleSession(returnTo, refresh_token, user_metadata.user_status);
  };

  const handleLoginWithGg = () => {
    localStorage.setItem("isGoogleLogin", "true");
    localStorage.setItem("isLogin", "true");
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo =
      urlParams.get("returnto") || localStorage.getItem("loginCallBackUrl");
    if (returnTo) {
      localStorage.setItem("loginCallBackUrl", returnTo);
    }
    fetchGetAuthorize("login", true, "google-oauth2");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlerSubmit();
    }
  };
const ResetPasswordNotice = () => (
  <div className="of-transferred">
    【パスワード再設定のお願い】
    <br />
    ※2025年4月20日（宣伝会議オンライン）、2025年9月27日（環境ビジネスオンライン）のサイト統合に伴い、
    セキュリティ強化の一環でパスワードルールを変更いたしました。
    <Link to="/user/guest/password_remind">
      初回ログイン時にパスワードの再設定をお願いしております。
    </Link>
    ログインできない方は、大変お手数ですが
    <Link to="/user/guest/password_remind">
      こちらからパスワードを再設定
    </Link>
    いただき、登録情報の入力不足がないか、ご確認のほどよろしくお願いいたします。
  </div>
);

  return (
    <div className="login-page">
      <section>
        <div className="l-head">
          <div className="step-info">
            <div className="page-title">
              <span>会員ログイン</span>
            </div>
          </div>
        </div>
      </section>
      <section className="login-form">
        <div>
          <div className="form-items">
            {errors.loginErr && isShowErrLogin ? (
              <div className="login-err">
                <span>{errors.loginErr}</span>
                <Close onClick={handleClickVisibleErrLogin} />
              </div>
            ) : (
              <></>
            )}
         
            <ResetPasswordNotice/>
            
            <div className="form-item">
              <dt className="fi-label">
                <label>
                  <span className="text">メールアドレス</span>
                  {/* <span className="item_required">{t("general.required")}</span> */}
                </label>
              </dt>
              <dd className="fi-control">
                <Input
                  inputRef={emailInputRef}
                  validateMess={errors.email}
                  error={errors.email}
                  placeholder="メールアドレス"
                  name="email"
                  id="email"
                  type="email"
                  value={state.email}
                  onChange={(value) => {
                    handlerChangeValue("email", value);
                  }}
                  onKeyDown={handleKeyDown}
                  withChecked={true}
                />
              </dd>
            </div>
            <div className="form-item">
              <dt className="fi-label">
                <label>
                  <span className="text">パスワード</span>
                  {/* <span className="item_required">{t("general.required")}</span> */}
                </label>
              </dt>
              <dd className="fi-control" style={{ position: "relative" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  error={errors.password}
                  placeholder="パスワード"
                  name="password"
                  id="password"
                  value={state.password}
                  onKeyDown={handleKeyDown}
                  onChange={(value) => {
                    handlerChangeValue("password", value);
                  }}
                />

                <img
                  className="toggle-password-icon"
                  src={showPassword ? "/images/eye-slash-solid.svg" : "/images/eye-solid.svg"}
                  onClick={() => setShowPassword(!showPassword)}
                  alt="toggle password"
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    width: "20px",
                    height: "20px",
                    cursor: "pointer",
                    opacity: 0.7,
                  }}
                />
              </dd>
            </div>
            <div className="buttons mt32">
              <button
                className="btn-submit"
                onClick={handlerSubmit}
              >
                <span>ログイン</span>
              </button>
            </div>
            <div className="to-forgot-pass">
              <Link to={Routes.PASSWORDREMIND}>
                <span>{t("features.auth.forgotPassword")}</span>
                <span>
                  <SolidArrow />
                </span>
              </Link>
            </div>
            <div className="label-border">
              <div className="label-border-inner">
                {t("features.auth.labelBorder")}
              </div>
            </div>
            <div className="center mb32 mt8">
              <a
                onClick={handleLoginWithGg}
                href="#"
                className="sns-btn-sm sns-btn-sm-google"
              ></a>
            </div>
          </div>
        </div>
        <h2 className="panel-inner-tit mt-5">
          <img src="/images/ico-new-user.png" className="ico-new-user" />
          まだ会員登録されていないお客様
        </h2>
        <div>
          <div className="to-register">
            <Link to={Routes.REGISTER}>
              <span>新規会員登録はこちら</span>
              <span>
                <SolidArrow />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
